import { useTranslate } from "../../../../customHooks";
import { usePermissions } from '../../../../customHooks';
import { useCallback, useMemo, useState } from "react";
import { toast } from "../../../../components";
import { isEmpty } from 'lodash';
import { useUpdateFirewallMutation, useLazyGetFirewallByIdQuery } from "../../../../redux/services/firewalls-service";
import { extractFieldsWithConfig } from "../../../../utils/utils";
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Button, Card, CardBody, CardHeader, LoadingPanel, Tooltip } from "@panwds/react-ui";
import TabPageHeader from "./TabPageHeader";
import { produce } from "immer";
import fwStyles from "../../FirewallEdit.module.scss";
import { FirewallUpdateBanner } from "../../FirewallUpdateBanner";
import { InfoIcon } from "@panwds/icons";
import { FirewallPrivateAccessTable } from '../Edit';
import { PrivateAccessCreate } from "./PrivateAccessComponent/PrivateAccessCreate";
import { PrivateAccessEdit } from "./PrivateAccessComponent/PrivateAccessEdit";
import { PRIVATE_ACCESS_CONFIG } from "./PrivateAccessComponent/constants";
import { FirewallPrivateAccessProps, PrivateAccessConfig } from "./PrivateAccessComponent/types";
import { PrerequisitesCard } from "./PrivateAccessComponent/PrerequisitesCard";
import { determinePolicyManaged, isFirewallUpdating, PolicyManagerType } from "../../firewallsUtil";

type PanelType = "" | "createPrivateAccess" | "editPrivateAccess";

const FirewallPrivateAccess = (props: FirewallPrivateAccessProps) => {
    const { canEdit, firewallData, isLoading, refreshFirewallData } = props;
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const region = useMemo(() => new URLSearchParams(location.search).get('region'), [location.search]);
    const firewallUpdating = isFirewallUpdating(firewallData?.Status?.FirewallStatus);
    const policyManager = useMemo(() => determinePolicyManaged(firewallData) as PolicyManagerType, [firewallData]);
    const [sidePanel, setSidePanel] = useState<PanelType>("");
    const [editPrivateAccessRecord, setEditPrivateAccessRecord] = useState<PrivateAccessConfig | null>(null);
    const [isPrerequisitesCollapsed, setIsPrerequisitesCollapsed] = useState(false);
    const [updaFirewall] = useUpdateFirewallMutation();
    const [fetchFirewall] = useLazyGetFirewallByIdQuery();

    // Memoized function to transform the default form values
    const transformFormDefaultValue = useCallback((privateAccessConfigData) => {
        if (isEmpty(privateAccessConfigData)) {
            return undefined;
        }
        // Extract fields from firewall describe response based on the config.
        // Extracts fields needed for firewall private access form.
        const newStateWithConfig = extractFieldsWithConfig(privateAccessConfigData, PRIVATE_ACCESS_CONFIG);

        // Merge PvtAccess from Firewall with Status from Status.PvtAccessStatus by matching Name
        const pvtAccessWithStatus = (newStateWithConfig.Firewall?.PvtAccess || []).map((item: PrivateAccessConfig) => {
            const statusEntry = newStateWithConfig.Status?.PvtAccessStatus?.find(
                (status: { Name: string; Status: string }) => status.Name === item.Name
            );
            return {
                ...item,
                Status: statusEntry?.Status || translate('generic.pending'),
            };
        });

        return {
            Firewall: {
                ...newStateWithConfig.Firewall,
                PvtAccess: pvtAccessWithStatus,
            }
        }
    }, [translate]);

    const transformFormSubmitValue = useCallback((formData: Record<string, any>) => {
        if (isEmpty(formData)) {
            return;
        }

        // Strip Status field from PvtAccess array (server-managed, not sent in requests)
        const pvtAccessWithoutStatus = (formData.Firewall?.PvtAccess || []).map((item: PrivateAccessConfig) => {
            const { Status, ...rest } = item;
            return rest;
        });

        const transformedData = {
            ...formData.Firewall,
            PvtAccess: pvtAccessWithoutStatus,
        }

        return transformedData;
    }, []);

    const formMethods = useForm({defaultValues: transformFormDefaultValue(firewallData)});
    const {
        control, setValue, getValues,
    } = formMethods;

    const onSubmit = useCallback(
        async (data) => {
            try {
                const fwid = firewallData?.Firewall?.FirewallId;

                let payload = {
                    ...transformFormSubmitValue(data),
                    FirewallId: fwid,
                    Region: region,
                };

                if (fwid && region) {
                    const latestFirewallData = await fetchFirewall({ fwid, region }).unwrap();
                    payload = {
                        ...payload,
                        UpdateToken: latestFirewallData?.Firewall?.UpdateToken,
                        DeploymentUpdateToken: latestFirewallData?.Firewall?.DeploymentUpdateToken,
                    };
                }

                const result = await updaFirewall({ payload }).unwrap();
                toast.success(translate(`resources.firewallsV2.privateAccess.privateAccessUpdateSuccess`), { toastId: 'update-firewall-private-access' });
                if (result) {
                    await refreshFirewallData();
                    setSidePanel("");
                }
            }
            catch (error: any) {
                toast.error(error?.error || error?.message || 'Something went wrong', { toastId: "update-firewall-private-access" });
                setSidePanel("");
            }
        }, [updaFirewall, transformFormSubmitValue, translate, refreshFirewallData, fetchFirewall, firewallData, region]
    );

    const createPrivateAccess = useCallback(
        async (data: PrivateAccessConfig) => {
            const currentFirewallData = transformFormDefaultValue(firewallData);
            if (currentFirewallData) {
                currentFirewallData.Firewall.PvtAccess =
                    [...currentFirewallData.Firewall.PvtAccess, data];
                onSubmit(currentFirewallData);
            }
            setSidePanel("");
        }, [transformFormDefaultValue, firewallData, onSubmit]
    );

    const deletePrivateAccess = useCallback(
        (row: PrivateAccessConfig) => {
            const currentFirewallData = transformFormDefaultValue(firewallData);
            if (currentFirewallData) {
                // Filter out the entry where Name matches row.Name
                currentFirewallData.Firewall.PvtAccess = currentFirewallData.Firewall.PvtAccess.filter(
                    (config: PrivateAccessConfig) => config?.Name !== row?.Name
                );
                onSubmit(currentFirewallData);
            }
        }, [transformFormDefaultValue, firewallData, onSubmit]
    );

    const editPrivateAccess = useCallback(
        async (newPrivateAccessConfigData: PrivateAccessConfig) => {
            const currentPvtAccess = getValues("Firewall.PvtAccess");
            const updatedState = produce(currentPvtAccess, (draft: PrivateAccessConfig[]) => {
                const index = draft.findIndex((item: PrivateAccessConfig) => item.Name === newPrivateAccessConfigData?.Name);
                if (index !== -1) {
                  draft[index] = newPrivateAccessConfigData;
                }
            });
            setValue("Firewall.PvtAccess", updatedState, {shouldDirty: true});
            setSidePanel("");
        }, [setValue, getValues]
    );

    const setEditPrivateAccessData = useCallback(
        async (row: PrivateAccessConfig) => {
            setEditPrivateAccessRecord(row);
            setSidePanel("editPrivateAccess");
        }, []
    );

    return (
        <div className="tw-relative">
            {isLoading &&
                <div className="tw-absolute tw-inset-0 tw-right-0 tw-flex tw-justify-center tw-items-center tw-backdrop-blur-sm tw-z-50 tw-bg-white/70">
                    <div className="tw-w-6 tw-h-6">
                        <LoadingPanel />
                    </div>
                </div>
            }
            {!canEdit &&
                <FirewallUpdateBanner content={translate('resources.firewallsV2.cantEditFirewall')} />
            }
            {firewallUpdating &&
                <FirewallUpdateBanner refresh={refreshFirewallData} />
            }
            <TabPageHeader title={translate(`resources.firewallsV2.privateAccess.title`)} addClassName="tw-pb-4">
                <Tooltip label={translate('resources.firewallsV2.privateAccess.titleTooltip')}>
                    <InfoIcon size="sm" className="tw-text-disabled"/>
                </Tooltip>
            </TabPageHeader>
            <div>
                <PrerequisitesCard
                    isCollapsed={isPrerequisitesCollapsed}
                    onToggle={() => setIsPrerequisitesCollapsed(!isPrerequisitesCollapsed)}
                    firewallData={firewallData}
                    policyManager={policyManager}
                />
                <div className="tw-pt-4">
                    <FormProvider {...formMethods}>
                        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                            <Card>
                                <CardHeader
                                    title={<span className="tw-font-medium">{`${translate('resources.firewallsV2.privateAccess.resourceConfigurations')}`}</span>}
                                    actions={<>
                                        <Button
                                            appearance="primary"
                                            addClassName="tw-mr-2"
                                            onClick={() => setSidePanel("createPrivateAccess")}
                                            disabled={!canEdit || !permissions?.UpdateFirewall || firewallUpdating}
                                        >
                                            {translate(`generic.addNew`)}
                                        </Button>
                                    </>}
                                />
                                <CardBody>
                                  <>
                                    <Controller
                                        name="Firewall.PvtAccess"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <FirewallPrivateAccessTable
                                                setEditPrivateAccessData={setEditPrivateAccessData}
                                                deletePrivateAccess={deletePrivateAccess}
                                                disabled={!canEdit || !permissions?.UpdateFirewall || firewallUpdating}
                                                {...field}
                                                label="Custom Field"
                                            />
                                        )}
                                    />
                                  </>
                                </CardBody>
                            </Card>
                        </form>
                        {sidePanel === 'createPrivateAccess' && <PrivateAccessCreate
                            close={() => setSidePanel("")}
                            createPrivateAccess={createPrivateAccess}
                            currentPrivateAccess={transformFormDefaultValue(firewallData)}
                            disabled={firewallUpdating}
                            policyManager={policyManager}
                        />
                        }
                        {sidePanel === 'editPrivateAccess' && editPrivateAccessRecord && <PrivateAccessEdit
                            close={() => setSidePanel("")}
                            editPrivateAccess={editPrivateAccess}
                            editPrivateAccessData={editPrivateAccessRecord}
                            disabled={firewallUpdating}
                        />
                        }
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};export default FirewallPrivateAccess;
